import { useEffect, useState } from "react";
import { Button, DatePicker, Table, Tooltip } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import MainButton from "../Buttons/MainButton";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import ROUTES from "router/NewRoutes";

const GridTable = ({
  loading,
  columns,
  dataSource,
  rowId,
  allowExport = false,
  onExport,
  isExporting,
  title,
  onChange,
  currentPage,
  totalCount,
  onPageChange,
  rowSelection,
  className = "",
  scroll = { x: 100 },
  bordered = false,
  onPageSizeChange,
  pageSize,
  showSocialOrdersBtns,
  scrapSalesRequestVideos = false,
  isDisplayVideosButton = false,
  onDisplayVideosModal,
  onCourierAssigning,
  disableAssignCourier,
  onUserStatusAssigning,
  disableUserStatus,
  showTableDateFilter,
  onDateFilter,
  dateFilterValue,
  summary,
  showMarketplaceBtns,
  onDistrictAssigning,
  disableAssignDistrict,
  onMakeRoute,
  disableMakeRoute,
  showExportDateFilter = false,
  exportDateFilterValue,
  onExportDateFilter,
  disableExport = false,
}) => {
  const [filteredData, setFilteredData] = useState([]);
  const [totalPrice, setTotalPrice] = useState(0);
  const { RangePicker } = DatePicker;

  const { t } = useTranslation();
  const location = useLocation();

  useEffect(() => {
    if (title === "Orders" && dataSource) {
      const arr = filteredData.length > 0 ? filteredData : dataSource;
      const totalPrice = arr.reduce(
        (pre, curr) => pre + parseFloat(curr.price),
        0,
      );
      setTotalPrice(totalPrice.toFixed(2));
    }
  }, [filteredData, dataSource, title]);

  return (
    <>
      <div className="d-flex justify-content-end align-items-start flex-wrap gap-2">
        {showSocialOrdersBtns ? (
          <>
            <Button
              onClick={onDistrictAssigning}
              htmlType="button"
              disabled={disableAssignDistrict}
              className="main-header__plus-button border-0 outline-none shadow-none mb-3"
              icon={<PlusOutlined />}
            >
              Assign To District
            </Button>
            <Button
              onClick={onCourierAssigning}
              htmlType="button"
              disabled={disableAssignCourier}
              className="main-header__plus-button border-0 outline-none shadow-none mb-3"
              icon={<PlusOutlined />}
            >
              Assign To Courier
            </Button>
            <Button
              onClick={onUserStatusAssigning}
              htmlType="button"
              disabled={disableUserStatus}
              className="main-header__plus-button border-0 outline-none shadow-none mb-3"
              icon={<PlusOutlined />}
            >
              Bulk Change Customer Status
            </Button>
            <Button
              onClick={onMakeRoute}
              htmlType="button"
              disabled={disableMakeRoute}
              className="main-header__plus-button border-0 outline-none shadow-none mb-3"
              icon={<PlusOutlined />}
            >
              Make a Route
            </Button>
          </>
        ) : showMarketplaceBtns ? (
          <Button
            onClick={showMarketplaceBtns.onOpenChangeStatusModal}
            htmlType="button"
            disabled={showMarketplaceBtns.disableBtn}
            className="main-header__plus-button border-0 outline-none shadow-none mb-3"
          >
            Change Status
          </Button>
        ) : (
          ""
        )}
        {scrapSalesRequestVideos && (
          <Tooltip
            title={
              isDisplayVideosButton
                ? "Display courier and stock admin videos"
                : "Filter by Courier name and request date to display courier and stock admin videos"
            }
          >
            <Button
              onClick={onDisplayVideosModal}
              htmlType="button"
              disabled={!isDisplayVideosButton}
              className="grid-table__export-btn font--bold"
              style={{ color: "#589559" }}
            >
              Display Videos
            </Button>
          </Tooltip>
        )}
        {allowExport && showExportDateFilter && (
          <div className="d-flex align-items-center gap-2 mb-2">
            <span className="fw-bold">Export Range:</span>
            <RangePicker
              value={exportDateFilterValue}
              allowClear
              popupClassName="main-date-picker"
              inputReadOnly={false}
              onChange={onExportDateFilter}
              onKeyDown={(e) => e.preventDefault()}
              placeholder={["From Date", "To Date"]}
            />
          </div>
        )}
        {allowExport ? (
          <div className="d-flex justify-content-end mb-2">
            <MainButton
              bg="white"
              textClr="primary-1"
              borderClr="primary-3"
              textSize={10}
              text={t("common.exportToExcel")}
              type="button"
              disabled={dataSource?.length === 0 || disableExport}
              loading={isExporting}
              onClick={() => {
                const dataToExport =
                  filteredData.length > 0 ? filteredData : dataSource;
                if (dataToExport.length > 0) {
                  onExport(dataToExport);
                }
              }}
              className="grid-table__export-btn font--bold"
            />
          </div>
        ) : (
          ""
        )}
      </div>
      {title === "Orders" ? (
        <p className="text-white">
          <span className="fw-bold">Total Price:</span> {totalPrice} EGP
        </p>
      ) : (
        ""
      )}
      {showTableDateFilter ? (
        <div className="d-flex justify-content-end mb-2">
          <RangePicker
            value={dateFilterValue}
            allowClear
            popupClassName="main-date-picker"
            inputReadOnly={false}
            onChange={(dates) => {
              onDateFilter(dates);
            }}
            onKeyDown={(e) => e.preventDefault()}
          />
        </div>
      ) : (
        ""
      )}
      <Table
        summary={summary}
        loading={loading}
        columns={columns}
        dataSource={dataSource}
        rowKey={(row) => row[rowId]}
        scroll={scroll}
        bordered={bordered}
        size="small"
        className={`grid-table ${className}`}
        rowClassName={(record, index) => {
          if (
            record.isAcceptedQuantity === false &&
            location.pathname.replace("/", "") === ROUTES.COURIER_WEIGHTS
          ) {
            return "row-rejected";
          }
          return index % 2 === 0 ? "background-even-records" : "";
        }}
        tableLayout="auto"
        rowSelection={rowSelection}
        onChange={(pagination, filters, sorter, extra) => {
          if (extra.action === "filter") {
            setFilteredData(extra.currentDataSource);
          }
          if (onChange) {
            onChange(pagination, filters, sorter, extra);
          }
        }}
        pagination={{
          responsive: true,
          current: currentPage,
          total: totalCount,
          defaultPageSize: 10,
          pageSize: pageSize,
          onChange: onPageChange,
          onShowSizeChange: onPageSizeChange,
        }}
      />
    </>
  );
};

export default GridTable;
